// Restvoorraad-I/O. Onderdeel van de additive api-split (story 1.5): nieuwe I/O leeft
// in lib/api/*, ge-barreld via lib/api.ts. Importeert supabase direct (geen circulaire
// import via api.ts). Reserve/afboek-mutaties komen in epic 4.
import { supabase } from '../supabase';

/** Telt de regels die fysiek beschikbaar zijn (voor dashboard-stat, vervangt .eq('status','available')). */
export async function countAvailableInventory(): Promise<number> {
  const { count, error } = await supabase
    .from('gift_inventory')
    .select('id', { count: 'exact', head: true })
    .gt('quantity_available', 0);
  if (error) throw error;
  return count ?? 0;
}

/** Reserveert een aantal uit een voorraad-lot (FR-7.7); de afgeleide status volgt vanzelf. */
export async function reserveFromStock(lotId: string, qty: number): Promise<void> {
  const { data: lot, error: e1 } = await supabase
    .from('gift_inventory')
    .select('quantity_available, quantity_reserved')
    .eq('id', lotId)
    .single();
  if (e1) throw e1;
  const reserved = (lot?.quantity_reserved ?? 0) + qty;
  const { error } = await supabase
    .from('gift_inventory')
    .update({ quantity_reserved: reserved })
    .eq('id', lotId);
  if (error) throw error;
}

export interface FreeStockLot {
  lotId: string;
  free: number;
}

/** Vrije voorraad (beschikbaar - gereserveerd) per product, voor de StockUsePicker op het inkoopmoment. */
export async function fetchFreeStockByProduct(
  productIds: string[]
): Promise<Map<string, FreeStockLot[]>> {
  const result = new Map<string, FreeStockLot[]>();
  if (productIds.length === 0) return result;
  const { data, error } = await supabase
    .from('gift_inventory')
    .select('id, gift_product_id, quantity_available, quantity_reserved, written_off_at')
    .in('gift_product_id', productIds)
    .is('written_off_at', null)
    .order('acquired_date', { ascending: true });
  if (error) throw error;
  for (const row of data ?? []) {
    const free = (row.quantity_available ?? 0) - (row.quantity_reserved ?? 0);
    if (free <= 0) continue;
    const list = result.get(row.gift_product_id) ?? [];
    list.push({ lotId: row.id, free });
    result.set(row.gift_product_id, list);
  }
  return result;
}

/** Reserveert een aantal van een product over de beschikbare lots (FIFO op binnenkomst). */
export async function reserveProductStock(lots: FreeStockLot[], qty: number): Promise<void> {
  let remaining = qty;
  for (const lot of lots) {
    if (remaining <= 0) break;
    const take = Math.min(lot.free, remaining);
    if (take > 0) {
      await reserveFromStock(lot.lotId, take);
      remaining -= take;
    }
  }
}

/** Schrijft een voorraad-lot af (FR-7.10): onomkeerbaar besluit met reden (NFR-3). */
export async function writeOffStock(lotId: string, reason: string): Promise<void> {
  const { error } = await supabase
    .from('gift_inventory')
    .update({ written_off_at: new Date().toISOString(), write_off_reason: reason })
    .eq('id', lotId);
  if (error) throw error;
}
